import React, { useContext, useState } from "react"
import { ThemeContext } from "styled-components"
import IcomoonReact from "icomoon-react/dist/IcomoonReact"
import { useDispatch, useSelector } from "react-redux"
import { useKeycloak } from "react-keycloak"
import { api } from "dicomweb-client"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import { useBeforeunload } from "react-beforeunload"
import { StudyUploadItem } from "./StudyUploadItem"
import {
  StyledStudyUploadWidget,
  StyledStudyUploadWidgetContent,
  StyledStudyUploadWidgetHeader,
} from "./styled/StyledStudyUploadWidget"
import icons from "../../../icons/cloud.json"
import iconsInfo from "../../../icons/iconsInfo"

const UPLOAD_THRESHOLD = 1024 * 1024 * 2;

export const StudyUploadWidget = () => {
  const maxConcurrentUploads = 2;
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentUploads, setCurrentUploads] = useState(0);
  const [keycloak] = useKeycloak();
  const themeContext = useContext(ThemeContext);
  const uploadingStudies = useSelector((state) => state.uploadingStudies);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // eslint-disable-next-line consistent-return
  useBeforeunload(() => {
    if (uploadingStudies.length > 0) {
      return 'There are studies being uploaded';
    }
  });

  const countPendingFiles = () => {
    let pendingFiles = 0;
    uploadingStudies.forEach((uploadingStudy) => {
      pendingFiles += uploadingStudy.files.length - uploadingStudy.nextFileIndex;
    });
    return pendingFiles;
  };

  const getDicomWebClient = () => (
    new api.DICOMwebClient({
      url: process.env.GATSBY_DICOM_WEB_URL,
      headers: {
        Authorization: `Bearer ${keycloak.token}`,
      },
    })
  );

  const uploadInstance = async (instance, studyInstanceUid) => {
    console.log('Uploading instance: ', instance.size);
    // Instantiate the DICOMweb client
    const client = getDicomWebClient();

    // Upload the instance to the server
    const response = await client.storeInstances({
      datasets: [instance],
      studyInstanceUID: studyInstanceUid,
    });

    // Check for errors
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  }

  const uploadMultipartInstance = async (instance, studyInstanceUid) => {
    // URL for the DICOMweb upload
    const url = `${process.env.GATSBY_DICOM_WEB_URL}/studies/${studyInstanceUid}`;

    // Set a unique boundary string for this multipart request
    const boundary = `${Date.now()}${Math.random().toString().slice(2)}`;

    // Create the multipart body parts
    const header = `--${boundary}\r\nContent-Type: application/dicom\r\n\r\n`;
    const footer = `\r\n--${boundary}--\r\n`;

    // Assemble the multipart body (binary large object)
    const blob = new Blob([header, instance, footer], { type: 'application/octet-stream' });

    // Make the multipart POST request
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
        'Accept': 'application/dicom+json',
        'Content-Type': `multipart/related;type="application/dicom";boundary=${boundary}`,
      },
      body: blob
    });

    // Check for errors
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  }

  const uploadNextFile = async () => {
    setCurrentUploads((prevCurrentUploads) => (prevCurrentUploads + 1));

    const upload = uploadingStudies.find((element) => (element.nextFileIndex <= element.files.length));

    const instance = upload.files[upload.nextFileIndex];

    dispatch({ type: 'UPDATE_STUDY_UPLOAD_FILE_INDEX', study: upload });

    try {
      if (instance.byteLength < UPLOAD_THRESHOLD) {
        await uploadInstance(instance, upload.studyInstanceUid);
      } else {
        await uploadMultipartInstance(instance, upload.studyInstanceUid);
      }
    } catch (e) {
      console.warn(e.message);
      toast(t('upload.study.instance.error', { studyDescription: upload.description }), { className: 'toast-error' });
    }
    dispatch({ type: 'UPDATE_STUDY_UPLOAD_FILES_COMPLETED', study: upload });
    setCurrentUploads((prevCurrentUploads) => (prevCurrentUploads - 1));
  };

  if (countPendingFiles() > 0 && currentUploads < maxConcurrentUploads) {
    uploadNextFile().then();
  }

  if (uploadingStudies.length === 0) {
    return null;
  }

  return (
    <StyledStudyUploadWidget>
      <StyledStudyUploadWidgetHeader onClick={() => { setIsMinimized(!isMinimized); }}>
        {t('uploading.n.studies', { count: uploadingStudies.length })}
        <IcomoonReact
          iconSet={icons}
          color={themeContext.colors.white}
          size={14}
          icon={isMinimized ? iconsInfo.arrow_up.name : iconsInfo.arrow_down.name}
        />
      </StyledStudyUploadWidgetHeader>
      <StyledStudyUploadWidgetContent isMinimized={isMinimized}>
        {
          uploadingStudies.map(((uploadingStudy) => (
            <StudyUploadItem key={uploadingStudy.studyInstanceUid} uploadingStudy={uploadingStudy} />
          )))
        }
      </StyledStudyUploadWidgetContent>
    </StyledStudyUploadWidget>
  );
};
